import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import SectionAccents from "@/components/SectionAccents";

const heights = ["aspect-[3/4]", "aspect-square", "aspect-[4/5]", "aspect-[3/4]", "aspect-square", "aspect-[4/5]"];

const Gallery = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openIdx, setOpenIdx] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/gallery");
                setItems(data.items || []);
            } catch {}
            setLoading(false);
        })();
    }, []);

    const close = useCallback(() => setOpenIdx(null), []);
    const prev = useCallback(() => setOpenIdx((i) => (i === null ? i : (i - 1 + items.length) % items.length)), [items.length]);
    const next = useCallback(() => setOpenIdx((i) => (i === null ? i : (i + 1) % items.length)), [items.length]);

    useEffect(() => {
        if (openIdx === null) return;
        const onKey = (e) => {
            if (e.key === "Escape") close();
            else if (e.key === "ArrowLeft") prev();
            else if (e.key === "ArrowRight") next();
        };
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [openIdx, close, prev, next]);

    const active = openIdx !== null ? items[openIdx] : null;

    return (
        <div className="relative max-w-7xl mx-auto px-6 py-14 overflow-hidden" data-testid="gallery-page">
            <SectionAccents variant="gallery" />
            <div className="text-xs uppercase tracking-widest text-brand-gold font-semibold">Wall of joy</div>
            <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tighter mt-1">The Gallery</h1>
            <p className="text-muted-foreground mt-2 max-w-xl">A curated look at recent miniatures from our workshop.</p>

            {loading ? (
                <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4">
                    {[...Array(6)].map((_, i) => <div key={i} className={`rounded-3xl bg-muted/30 animate-pulse ${heights[i % heights.length]}`} />)}
                </div>
            ) : items.length === 0 ? (
                <div className="mt-16 rounded-3xl glass p-14 text-center text-muted-foreground text-sm">The gallery is being curated. Check back soon 💛</div>
            ) : (
                <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4">
                    {items.map((item, i) => (
                        <motion.button
                            type="button"
                            key={item.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: (i % 6) * 0.05 }}
                            onClick={() => setOpenIdx(i)}
                            data-testid={`gallery-tile-${i}`}
                            aria-label={item.caption ? `Open ${item.caption}` : `Open gallery image ${i + 1}`}
                            className={`rounded-3xl overflow-hidden tilt-card ${heights[i % heights.length]} relative group text-left cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-primary`}
                        >
                            <img src={item.image_url} alt={item.caption || "Cherishables gallery"} className="w-full h-full object-cover" loading="lazy" />
                            {item.caption && (
                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-end p-4">
                                    <div className="text-white">
                                        <div className="text-sm font-semibold">{item.caption}</div>
                                        {item.category && <div className="text-[11px] uppercase tracking-widest opacity-80 mt-0.5">{item.category}</div>}
                                    </div>
                                </div>
                            )}
                        </motion.button>
                    ))}
                </div>
            )}

            <AnimatePresence>
                {active && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={close}
                        data-testid="gallery-lightbox"
                        className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
                    >
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); close(); }}
                            data-testid="gallery-lightbox-close"
                            aria-label="Close"
                            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        {items.length > 1 && (
                            <>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); prev(); }}
                                    data-testid="gallery-lightbox-prev"
                                    aria-label="Previous"
                                    className="absolute left-2 sm:left-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                                >
                                    <ChevronLeft className="w-6 h-6" />
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); next(); }}
                                    data-testid="gallery-lightbox-next"
                                    aria-label="Next"
                                    className="absolute right-2 sm:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                                >
                                    <ChevronRight className="w-6 h-6" />
                                </button>
                            </>
                        )}
                        <motion.div
                            key={active.id}
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            onClick={(e) => e.stopPropagation()}
                            className="max-w-5xl max-h-full flex flex-col items-center"
                        >
                            <img
                                src={active.image_url}
                                alt={active.caption || "Cherishables gallery"}
                                data-testid="gallery-lightbox-image"
                                className="max-w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl"
                            />
                            {(active.caption || active.category) && (
                                <div className="mt-4 text-center text-white/90">
                                    {active.caption && <div className="text-base font-semibold">{active.caption}</div>}
                                    {active.category && <div className="text-[11px] uppercase tracking-widest opacity-70 mt-1">{active.category}</div>}
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Gallery;
